/**
 * BM-H4 — Guide Reader (read-only)
 *
 * Renders one staff guide's markdown by key. Chrome mirrors the Policy Reader
 * (Brief 26a): header + back-link + formatted document body.
 *
 * The body is rendered through the shared MarkdownDoc surface — guides use
 * headings, tables, nested lists and links, and previously showed as raw
 * Markdown source in a <pre> block.
 */

import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, AlertCircle, RefreshCw } from "lucide-react";
import { guideLook } from "./guideLook";
import MarkdownDoc from "../components/MarkdownDoc";

const API = process.env.REACT_APP_BACKEND_URL || "";

// GUIDES-SCREENSHOTS (29 Sep 2026): guide screenshots are written as
// /api/hub/guides/images/... in the markdown. Where the SPA and the API are on
// different hosts, a bare /api src would hit the SPA host, so point it at API.
export function withBackendImages(markdown) {
  return String(markdown || "").replace(/\]\(\/api\/hub\/guides\/images\//g, `](${API}/api/hub/guides/images/`);
}

export default function GuideReader() {
  const { key } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const r = await fetch(`${API}/api/hub/guides/${encodeURIComponent(key)}`, {
          credentials: "include",
        });
        if (r.status === 404) throw new Error("Guide not found");
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [key]);

  if (loading) {
    return (
      <div data-testid="guide-reader-loading" className="p-6 text-txt-muted">
        <RefreshCw className="inline-block animate-spin mr-2" size={16} />
        Loading {key}...
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="guide-reader-error" className="p-6 text-red-400">
        <AlertCircle className="inline-block mr-2" size={16} />
        {error}
        <div className="mt-2">
          <Link to="/guides" className="text-teal hover:underline">
            <ArrowLeft className="inline-block mr-1" size={14} />
            Back to Guides Library
          </Link>
        </div>
      </div>
    );
  }
  if (!data || !data.markdown) {
    return (
      <div data-testid="guide-reader-empty" className="p-6 text-txt-muted">
        No guide content.
      </div>
    );
  }

  const look = guideLook(key);
  return (
    <div data-testid="guide-reader-page" className="p-6 max-w-4xl mx-auto">
      <div className="mb-4">
        <Link to="/guides" className="text-sm text-teal hover:underline">
          <ArrowLeft className="inline-block mr-1" size={14} />
          Back to Guides Library
        </Link>
      </div>

      <header className="cm-hero relative mb-6 overflow-hidden rounded-2xl border border-white/10 p-5 shadow-xl shadow-indigo-950/40" style={look.style}>
        <div aria-hidden className="agents-orb absolute -right-16 -top-20 h-64 w-64 rounded-full opacity-60"
          style={{ background: "radial-gradient(circle, var(--c2), transparent 65%)" }} />
        <div className="relative flex items-center gap-4">
          <div className="cm-tile flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ring-1 ring-white/20">
            <look.Icon size={24} className="text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold agents-title" data-testid="guide-reader-title">
              {data.title}
            </h1>
            {data.description && (
              <p className="mt-0.5 text-sm text-indigo-100/80" data-testid="guide-reader-description">
                {data.description}
              </p>
            )}
          </div>
        </div>
      </header>

      <section className="mb-6">
        <MarkdownDoc
          markdown={withBackendImages(data.markdown)}
          testId="guide-reader-body"
          className="rounded-xl border border-white/[0.07] bg-navy-card p-6 shadow-lg"
        />
      </section>
    </div>
  );
}
